import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn } from "@/components/kit";
import { Plus, Save, X, Trash2, Image as ImageIcon, Pencil, Layers, Copy, ArrowUp, ArrowDown, GripVertical, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { STARTER_SCOPES } from "@/lib/starterScopes";

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function ScopeTemplates() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const load = () => api.get("/scope-templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.name.trim()) return toast.error("Name your template");
    const body = { name: edit.name, items: edit.items };
    try {
      if (edit.id) await api.put(`/scope-templates/${edit.id}`, body);
      else await api.post("/scope-templates", body);
      toast.success("Template saved"); setEdit(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await api.delete(`/scope-templates/${t.id}`); toast.success("Deleted"); load(); };

  const duplicate = async (t) => {
    const items = (t.items || []).map((it) => ({ title: it.title || "", description: it.description || "", image_url: it.image_url || "" }));
    try {
      const r = await api.post("/scope-templates", { name: `${t.name} (Copy)`, items });
      toast.success("Template duplicated");
      load();
      setEdit({ id: r.data.id, name: r.data.name, items: r.data.items || [] });
    } catch { toast.error("Duplicate failed"); }
  };

  const copyFrom = (id) => {
    const src = list.find((t) => t.id === id);
    if (!src) return;
    const items = (src.items || []).map((it) => ({ title: it.title || "", description: it.description || "", image_url: it.image_url || "" }));
    setEdit((e) => ({ ...e, items: [...e.items, ...items] }));
    toast.success(`Added ${items.length} steps from "${src.name}"`);
  };

  const setItem = (i, k, v) => setEdit((e) => ({ ...e, items: e.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setEdit((e) => ({ ...e, items: [...e.items, { title: "", description: "", image_url: "" }] }));
  const rmItem = (i) => setEdit((e) => ({ ...e, items: e.items.filter((_, idx) => idx !== i) }));
  const moveItem = (from, to) => setEdit((e) => {
    if (to < 0 || to >= e.items.length) return e;
    const items = [...e.items];
    const [m] = items.splice(from, 1);
    items.splice(to, 0, m);
    return { ...e, items };
  });
  const onDrop = (to) => { if (dragIdx !== null && dragIdx !== to) moveItem(dragIdx, to); setDragIdx(null); };

  const addStarter = async (sc) => {
    try {
      const items = sc.items.map((i) => ({ ...i }));
      const r = await api.post("/scope-templates", { name: sc.name, items });
      toast.success(`Added "${sc.name}" — edit it below`);
      load();
      setEdit({ id: r.data.id, name: r.data.name, items: r.data.items || [] });
    } catch { toast.error("Could not add starter template"); }
  };

  return (
    <div>
      <PageHeader title="Scope Templates" subtitle="Reusable, pictured Scope-of-Work lists you can drop onto any proposal"
        actions={<Btn variant="red" onClick={() => setEdit({ name: "", items: [{ title: "", description: "", image_url: "" }] })} testid="new-template-btn"><Plus className="w-4 h-4" /> New Template</Btn>} />

      <div className="mb-card p-5 mb-6" data-testid="starter-library">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-brandred" /><span className="font-display text-lg font-bold uppercase text-navy">Starter Library</span></div>
        <div className="text-xs text-slate-500 mb-4">Ready-made roofing scopes — click one to create an editable copy you can tweak.</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {STARTER_SCOPES.map((sc) => (
            <button key={sc.name} data-testid={`starter-${sc.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => addStarter(sc)}
              className="text-left border-2 border-slate-200 rounded-xl p-3 hover:border-navy transition-colors group">
              <div className="flex flex-wrap gap-1 mb-2">
                {sc.items.slice(0, 4).map((it, i) => it.image_url ? <img key={i} src={it.image_url} alt="" className="w-9 h-9 rounded object-cover border border-slate-200" /> : <div key={i} className="w-9 h-9 rounded bg-slate-100" />)}
              </div>
              <div className="font-bold text-navy text-sm group-hover:text-brandred flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> {sc.name}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{sc.items.length} steps · {sc.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => (
          <div key={t.id} className="mb-card p-5" data-testid={`template-${t.id}`}>
            <div className="flex justify-between items-start">
              <div><div className="font-display text-lg font-bold uppercase text-navy flex items-center gap-2"><Layers className="w-4 h-4" /> {t.name}</div><div className="text-xs text-slate-500 mt-0.5">{t.items?.length || 0} steps</div></div>
              <div className="flex gap-2">
                <button data-testid={`dup-template-${t.id}`} onClick={() => duplicate(t)} title="Duplicate" className="text-slate-400 hover:text-navy"><Copy className="w-4 h-4" /></button>
                <button data-testid={`edit-template-${t.id}`} onClick={() => setEdit({ id: t.id, name: t.name, items: t.items || [] })} className="text-slate-400 hover:text-navy"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`del-template-${t.id}`} onClick={() => del(t)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(t.items || []).slice(0, 6).map((it, i) => it.image_url ? <img key={i} src={it.image_url} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" /> : <div key={i} className="w-10 h-10 rounded bg-slate-100" />)}
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-slate-400 text-sm col-span-full">No templates yet. Create one to get a pictured Scope of Work.</div>}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEdit(null)}>
          <div className="mb-card p-6 w-full max-w-2xl animate-scale-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="template-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl font-bold uppercase text-navy">{edit.id ? "Edit" : "New"} Template</h3><button onClick={() => setEdit(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <label className="text-xs font-semibold uppercase text-slate-500">Template Name</label>
            <input data-testid="template-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Roof Replacement" className="mt-1 mb-4 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
            {list.filter((t) => t.id !== edit.id).length > 0 && (
              <div className="mb-4 flex items-center gap-2 bg-slate-50 border-2 border-slate-100 rounded-xl p-3">
                <Copy className="w-4 h-4 text-navy shrink-0" />
                <span className="text-xs font-semibold text-slate-500 shrink-0">Copy steps from</span>
                <select data-testid="copy-from-select" value="" onChange={(e) => { if (e.target.value) copyFrom(e.target.value); e.target.value = ""; }} className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy bg-white">
                  <option value="">Select a template…</option>
                  {list.filter((t) => t.id !== edit.id).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} steps)</option>)}
                </select>
              </div>
            )}
            <div className="space-y-3">
              {edit.items.map((it, i) => (
                <div key={i} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className={`flex gap-3 items-start border-2 rounded-xl p-3 ${dragIdx === i ? "border-navy bg-navy/5 opacity-60" : "border-slate-100"}`} data-testid={`template-item-${i}`}>
                  <div className="flex flex-col items-center gap-0.5 pt-1 shrink-0">
                    <span className="cursor-grab text-slate-300 hover:text-slate-500" title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
                    <button data-testid={`ti-up-${i}`} onClick={() => moveItem(i, i - 1)} disabled={i === 0} className="text-slate-400 hover:text-navy disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                    <button data-testid={`ti-down-${i}`} onClick={() => moveItem(i, i + 1)} disabled={i === edit.items.length - 1} className="text-slate-400 hover:text-navy disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
                  </div>
                  <div className="w-16 h-16 rounded-lg border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
                    {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input data-testid={`ti-title-${i}`} value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} placeholder="Step title (e.g. Install Synthetic Felt)" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy" />
                    <textarea data-testid={`ti-desc-${i}`} rows={2} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Description" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-navy" />
                    <div className="flex items-center gap-2">
                      <input data-testid={`ti-file-${i}`} type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 3 * 1024 * 1024) return toast.error("Image under 3 MB"); setItem(i, "image_url", await readFile(file)); }} className="text-xs" />
                      {it.image_url && <button onClick={() => setItem(i, "image_url", "")} className="text-[11px] text-brandred font-semibold">Remove</button>}
                    </div>
                  </div>
                  <button data-testid={`ti-remove-${i}`} onClick={() => rmItem(i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button data-testid="add-template-item" onClick={addItem} className="text-sm text-navy font-semibold mt-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Add step</button>
            <button data-testid="save-template" onClick={save} className="w-full mt-4 bg-navy text-white font-bold py-3 rounded-xl border-2 border-navy flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save Template</button>
          </div>
        </div>
      )}
    </div>
  );
}
